import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StickerPack, StickerRef } from '@kinop/shared';
import { ApiError, importStickerPack, listStickerPacks, stickerUrl } from '../lib/api.js';
import {
  getStickerLibrary,
  installPack,
  rememberRecentSticker,
  toggleFavouriteSticker,
  uninstallPack,
  type StickerLibrary,
} from '../lib/storage.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface StickerPickerProps {
  onPick: (sticker: StickerRef) => void;
  /**
   * Что показываем: обычные стикеры или премиум-эмодзи.
   *
   * В телеграме это разные разделы, и смешивать их нельзя: эмодзи мельче,
   * их больше, и ищут их отдельно от стикеров.
   */
  kind: 'regular' | 'custom_emoji';
}

/** Служебные вкладки идут первыми и наборами не являются. */
type Tab =
  | { kind: 'recent' }
  | { kind: 'favourites' }
  | { kind: 'pack'; name: string }
  | { kind: 'add' };

/**
 * Панель стикеров.
 *
 * Сервер держит общий склад: сами файлы и состав наборов. А что из этого
 * показывать — своё у каждого: добавленные наборы, недавние и избранные
 * живут в браузере, рядом с аватаркой и счётчиками. Аккаунтов у нас нет, и
 * заводить их ради списка наборов незачем.
 */
export function StickerPicker({ onPick, kind }: StickerPickerProps): JSX.Element {
  const [packs, setPacks] = useState<StickerPack[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [library, setLibrary] = useState<StickerLibrary>(getStickerLibrary);
  const [tab, setTab] = useState<Tab | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const result = await listStickerPacks();
      // Наборы другого вида в этом разделе не показываем вовсе: они живут
      // в соседнем, и место здесь занимали бы зря.
      setPacks(result.packs.filter((pack) => sameKind(pack, kind)));
      setEnabled(result.enabled);
    } catch {
      setError('Не удалось получить наборы.');
    } finally {
      setIsLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Все стикеры сервера по номеру: по ним разворачиваются недавние и избранные. */
  const byId = useMemo(() => {
    const map = new Map<string, StickerRef>();
    for (const pack of packs) for (const sticker of pack.stickers) map.set(sticker.id, sticker);
    return map;
  }, [packs]);

  const installed = useMemo(
    () =>
      library.installed
        .map((name) => packs.find((pack) => pack.name === name))
        .filter((pack): pack is StickerPack => Boolean(pack)),
    [library.installed, packs],
  );

  const available = useMemo(
    () => packs.filter((pack) => !library.installed.includes(pack.name)),
    [library.installed, packs],
  );

  const resolve = useCallback(
    (ids: string[]): StickerRef[] =>
      ids.map((id) => byId.get(id)).filter((item): item is StickerRef => Boolean(item)),
    [byId],
  );

  const recent = resolve(library.recent);
  const favourites = resolve(library.favourites);

  const tabs: Tab[] = [
    ...(recent.length > 0 ? [{ kind: 'recent' } as Tab] : []),
    ...(favourites.length > 0 ? [{ kind: 'favourites' } as Tab] : []),
    ...installed.map((pack): Tab => ({ kind: 'pack', name: pack.name })),
    { kind: 'add' },
  ];

  // Открытая вкладка могла исчезнуть — например, набор убрали. Тогда
  // показываем первую существующую, а не пустоту от вкладки, которой нет.
  const active = tabs.find((item) => sameTab(item, tab)) ?? tabs[0] ?? { kind: 'add' };

  const send = (sticker: StickerRef): void => {
    setLibrary(rememberRecentSticker(sticker.id));
    onPick(sticker);
  };

  const addPack = async (): Promise<void> => {
    const value = draft.trim();
    if (!value || isAdding) return;

    setIsAdding(true);
    setError(null);
    try {
      const result = await importStickerPack(value);
      setDraft('');
      await load();
      if (result.pack) {
        setLibrary(installPack(result.pack.name));
        setTab({ kind: 'pack', name: result.pack.name });
      }
    } catch (addError) {
      setError(
        addError instanceof ApiError ? addError.message : 'Набор не добавился. Попробуйте ещё раз.',
      );
    } finally {
      setIsAdding(false);
    }
  };

  const activePack =
    active.kind === 'pack' ? (packs.find((pack) => pack.name === active.name) ?? null) : null;

  const shown =
    active.kind === 'recent'
      ? recent
      : active.kind === 'favourites'
        ? favourites
        : (activePack?.stickers ?? []);

  return (
    <div className="stickers">
      <div className="stickers__tabs" role="tablist" aria-label="Наборы стикеров">
        {tabs.map((item) => (
          <button
            key={tabKey(item)}
            type="button"
            role="tab"
            aria-selected={sameTab(item, active)}
            aria-label={tabLabel(item, packs)}
            title={tabLabel(item, packs)}
            className="pill"
            data-on={sameTab(item, active) ? '' : undefined}
            onClick={() => setTab(item)}
          >
            {tabTitle(item, packs)}
          </button>
        ))}
      </div>

      {active.kind === 'add' ? (
        <AddPanel
          enabled={enabled}
          available={available}
          draft={draft}
          isAdding={isAdding}
          placeholder={kind === 'custom_emoji' ? 't.me/addemoji/…' : 't.me/addstickers/…'}
          hint={
            kind === 'custom_emoji'
              ? 'Ссылка на набор премиум-эмодзи берётся в самом телеграме и выглядит как t.me/addemoji/… Их же можно поставить значком рядом с именем — в профиле.'
              : 'Ссылка на набор берётся в самом телеграме. Ещё можно переслать любой стикер боту — он добавит весь его набор.'
          }
          onDraft={setDraft}
          onSubmit={() => void addPack()}
          onInstall={(name) => {
            setLibrary(installPack(name));
            setTab({ kind: 'pack', name });
          }}
        />
      ) : (
        <>
          {/* Премиум-эмодзи мельче обычных стикеров: они и в телеграме идут
              вперемешку с текстом, а не отдельной картинкой. */}
          <div
            className={`stickers__grid ${kind === 'custom_emoji' ? 'stickers__grid--emoji' : ''}`}
          >
            {shown.map((sticker) => (
              <StickerButton
                key={sticker.id}
                sticker={sticker}
                favourite={library.favourites.includes(sticker.id)}
                onSend={() => send(sticker)}
                onToggleFavourite={() => setLibrary(toggleFavouriteSticker(sticker.id))}
              />
            ))}
          </div>

          {shown.length === 0 && !isLoading && (
            <p className="stickers__empty">
              {active.kind === 'recent'
                ? 'Здесь будут те, что вы отправляли.'
                : active.kind === 'favourites'
                  ? 'Избранных пока нет. Нажмите на звёздочку у стикера.'
                  : 'Набор пуст.'}
            </p>
          )}

          {activePack && (
            <div className="stickers__foot">
              <span className="stickers__pack-name">{activePack.title}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLibrary(uninstallPack(activePack.name))}
              >
                Убрать набор
              </Button>
            </div>
          )}
        </>
      )}

      {error && (
        <p className="stickers__error" role="alert">
          {error}
        </p>
      )}

      {isLoading && <p className="stickers__empty">Загружаем…</p>}
    </div>
  );
}

/**
 * Набор своего вида.
 *
 * У старых наборов, добавленных до разделения, вид мог не сохраниться —
 * тогда считаем их обычными стикерами: их подавляющее большинство.
 */
function sameKind(pack: StickerPack, kind: 'regular' | 'custom_emoji'): boolean {
  return (pack.kind === 'custom_emoji' ? 'custom_emoji' : 'regular') === kind;
}

function tabKey(tab: Tab): string {
  return tab.kind === 'pack' ? `pack:${tab.name}` : tab.kind;
}

function sameTab(a: Tab, b: Tab | null): boolean {
  return b ? tabKey(a) === tabKey(b) : false;
}

function tabTitle(tab: Tab, packs: StickerPack[]): string {
  if (tab.kind === 'recent') return '🕘';
  if (tab.kind === 'favourites') return '★';
  if (tab.kind === 'add') return '+';
  return packs.find((pack) => pack.name === tab.name)?.title ?? tab.name;
}

function tabLabel(tab: Tab, packs: StickerPack[]): string {
  if (tab.kind === 'recent') return 'Недавние';
  if (tab.kind === 'favourites') return 'Избранные';
  if (tab.kind === 'add') return 'Добавить набор';
  return tabTitle(tab, packs);
}

/**
 * Стикер в панели.
 *
 * Звёздочка появляется при наведении, а на телефоне — по долгому нажатию:
 * держать её видимой всё время значит закрыть ею сам стикер.
 */
function StickerButton({
  sticker,
  favourite,
  onSend,
  onToggleFavourite,
}: {
  sticker: StickerRef;
  favourite: boolean;
  onSend: () => void;
  onToggleFavourite: () => void;
}): JSX.Element {
  const timer = useRef<number | null>(null);
  const handled = useRef(false);

  const startPress = (): void => {
    handled.current = false;
    timer.current = window.setTimeout(() => {
      handled.current = true;
      onToggleFavourite();
    }, 500);
  };

  const endPress = (): void => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  return (
    <div className="stickers__cell" data-favourite={favourite ? '' : undefined}>
      <button
        type="button"
        className="stickers__item"
        aria-label={`Отправить стикер ${sticker.emoji ?? ''}`.trim()}
        onClick={() => {
          // Долгое нажатие уже сработало как «в избранное»: отправлять не нужно.
          if (handled.current) {
            handled.current = false;
            return;
          }
          onSend();
        }}
        onPointerDown={startPress}
        onPointerUp={endPress}
        onPointerLeave={endPress}
        onPointerCancel={endPress}
        onContextMenu={(event) => {
          event.preventDefault();
          onToggleFavourite();
        }}
      >
        <StickerImage sticker={sticker} />
      </button>

      <button
        type="button"
        className="stickers__star"
        data-on={favourite ? '' : undefined}
        aria-label={favourite ? 'Убрать из избранного' : 'В избранное'}
        aria-pressed={favourite}
        onClick={onToggleFavourite}
      >
        ★
      </button>
    </div>
  );
}

/** Вкладка «+»: добавить по ссылке или взять из уже известных серверу. */
function AddPanel({
  enabled,
  available,
  draft,
  isAdding,
  hint,
  placeholder,
  onDraft,
  onSubmit,
  onInstall,
}: {
  enabled: boolean;
  available: StickerPack[];
  draft: string;
  isAdding: boolean;
  hint: string;
  placeholder: string;
  onDraft: (value: string) => void;
  onSubmit: () => void;
  onInstall: (name: string) => void;
}): JSX.Element {
  if (!enabled) {
    return <p className="stickers__empty">Телеграм на этом сервере не подключён.</p>;
  }

  return (
    <div className="stickers__add-panel">
      <form
        className="stickers__add"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <Input
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          placeholder={placeholder}
          aria-label="Ссылка на набор"
        />
        <Button type="submit" size="sm" disabled={!draft.trim() || isAdding}>
          {isAdding ? 'Ищем…' : 'Добавить'}
        </Button>
      </form>

      <p className="stickers__hint">{hint}</p>

      {available.length > 0 && (
        <>
          <span className="mono-label">Уже на сервере</span>
          <div className="stickers__offer">
            {available.map((pack) => (
              <button
                key={pack.name}
                type="button"
                className="stickers__offer-item"
                onClick={() => onInstall(pack.name)}
              >
                {pack.stickers[0] && (
                  <StickerImage sticker={pack.stickers[0]} className="stickers__offer-face" />
                )}
                <span className="stickers__offer-title">{pack.title}</span>
                <span className="stickers__offer-add">добавить</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Картинка стикера.
 *
 * Три разных случая под одним видом. Обычные — картинка. Видеостикеры
 * телеграм отдаёт как .webm, их показывает <video>. Рисованные приходят в
 * .tgs — это Lottie под gzip, и для них нужен проигрыватель.
 */
export function StickerImage({
  sticker,
  className = '',
}: {
  sticker: StickerRef;
  className?: string;
}): JSX.Element {
  const label = sticker.emoji ?? 'стикер';
  const holder = useRef<HTMLSpanElement>(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const element = holder.current;
    if (!element) return;
    let visible = false;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setActive(visible && !document.hidden && !reduced.matches);
    const observer = new IntersectionObserver(entries => { visible = entries[0]?.isIntersecting ?? false; update(); });
    observer.observe(element);
    document.addEventListener('visibilitychange', update);
    reduced.addEventListener('change', update);
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', update); reduced.removeEventListener('change', update); };
  }, []);
  if (sticker.isVideo || sticker.isAnimated) return <span ref={holder} className={`sticker ${className}`}>
    {active ? sticker.isVideo ? <video className={`sticker ${className}`} src={stickerUrl(sticker.id)} autoPlay loop muted playsInline preload="none" aria-label={label} />
      : <AnimatedSticker sticker={sticker} className={className} label={label} />
      : <img className={`sticker ${className}`} src={`${stickerUrl(sticker.id)}?static=1`} alt={label} loading="lazy" decoding="async" />}
  </span>;

  return (
    <img
      className={`sticker ${className}`}
      src={stickerUrl(sticker.id)}
      alt={label}
      loading="lazy"
    />
  );
}

/**
 * Рисованный стикер.
 *
 * Проигрыватель весит больше полутора сотен килобайт, поэтому грузится
 * отдельным куском и только когда такой стикер действительно появился на
 * экране. В панели их полсотни разом: запускать все — значит занять
 * процессор рисованием того, чего человек не видит.
 *
 * Пока проигрыватель не загрузился — и если он не загрузился вовсе —
 * показывается неподвижное превью. Оно есть у каждого стикера, и лучше
 * неподвижная картинка, чем пустое место.
 */
function AnimatedSticker({
  sticker,
  className,
  label,
}: {
  sticker: StickerRef;
  className: string;
  label: string;
}): JSX.Element {
  const holderRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return;

    let cancelled = false;
    let visible = false;
    const abort = new AbortController();
    let animation: { destroy(): void } | null = null;
    // Загрузка асинхронная, а позвать её могут не раз: наблюдатель срабатывает
    // повторно, да и React в разработке прогоняет эффект дважды. Без этого
    // замка в контейнер попадали два проигрывателя, и брошенный застывал
    // поверх живого неподвижным кадром.
    let starting = false;

    const start = async (): Promise<void> => {
      if (starting || animation) return;
      starting = true;
      try {
        const [player, response] = await Promise.all([
          import('lottie-web/build/player/lottie_light'),
          // Сервер отдаёт .tgs как json под gzip, поэтому разбор бесплатный:
          // браузер разворачивает его по дороге сам.
          fetch(stickerUrl(sticker.id), { signal: abort.signal }),
        ]);
        if (!response.ok) throw new Error(`ответ ${response.status}`);
        const animationData: unknown = await response.json();
        if (cancelled || !visible || !holderRef.current) return;

        // Контейнер очищаем перед загрузкой: если что-то от прошлой попытки
        // всё же осталось, оно не должно пережить новую.
        holderRef.current.replaceChildren();
        animation = player.default.loadAnimation({
          container: holderRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData,
        });

        // Пока шла загрузка, стикер могли убрать с экрана.
        if (cancelled) {
          animation.destroy();
          animation = null;
          return;
        }
        setPlaying(true);
      } catch {
        // Остаётся превью — оно уже показано.
      } finally {
        starting = false;
      }
    };

    // Запускаем, только когда стикер виден. Уехал за пределы экрана —
    // останавливаем: иначе открытая панель молотит анимации без конца.
    const observer = new IntersectionObserver(
      (entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible && !animation) {
          void start();
        } else if (!visible && animation) {
          animation.destroy();
          animation = null;
          setPlaying(false);
        }
      },
      { rootMargin: '0px' },
    );
    observer.observe(holder);

    return () => {
      cancelled = true;
      abort.abort();
      observer.disconnect();
      animation?.destroy();
    };
  }, [sticker.id]);

  return (
    <div className={`sticker sticker--lottie ${className}`} role="img" aria-label={label}>
      {!playing && (
        <img
          className="sticker__still"
          src={`${stickerUrl(sticker.id)}?static=1`}
          alt=""
          loading="lazy"
        />
      )}
      <div className="sticker__canvas" ref={holderRef} />
    </div>
  );
}
